import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';

interface FeatureFlag {
  enabled: boolean;
  loading: boolean;
  meta?: any;
}

export function useFeatureFlag(workspaceId: string | undefined, featureKey: string): FeatureFlag {
  const { data, isLoading } = useQuery({
    queryKey: ['feature-flag', workspaceId, featureKey],
    queryFn: async () => {
      if (!workspaceId) return null;
      
      const { data, error } = await supabase
        .from('workspace_features')
        .select('enabled, meta')
        .eq('workspace_id', workspaceId)
        .eq('feature_key', featureKey)
        .single();
      
      if (error) {
        // Feature not found - default to enabled
        if (error.code === 'PGRST116') {
          return { enabled: true, meta: null };
        }
        throw error;
      }
      
      return data;
    },
    enabled: !!workspaceId,
    staleTime: 30_000,
    retry: 0,
  });

  return {
    enabled: data?.enabled ?? true, // Default to enabled if not found
    loading: isLoading,
    meta: data?.meta,
  };
}

// Helper hook for multiple feature flags
export function useFeatureFlags(workspaceId: string | undefined, featureKeys: string[]) {
  const { data, isLoading } = useQuery({
    queryKey: ['feature-flags', workspaceId, featureKeys],
    queryFn: async () => {
      if (!workspaceId) return {};
      
      const { data, error } = await supabase
        .from('workspace_features')
        .select('feature_key, enabled, meta')
        .eq('workspace_id', workspaceId)
        .in('feature_key', featureKeys);
      
      if (error) throw error;
      
      // Convert to object for easy lookup
      const result: Record<string, { enabled: boolean; meta?: any }> = {};
      
      // Set defaults for all requested features
      featureKeys.forEach(key => {
        result[key] = { enabled: true, meta: null };
      });
      
      // Override with actual data
      data.forEach(item => {
        result[item.feature_key] = {
          enabled: item.enabled,
          meta: item.meta,
        };
      });
      
      return result;
    },
    enabled: !!workspaceId && featureKeys.length > 0,
    staleTime: 30_000,
    retry: 0,
  });

  return {
    features: data || {},
    loading: isLoading,
  };
}